import { assert_equal, assert_hash_sha256_not_empty, assert_is_safe_number, assert_string_not_empty, assert_type_enum } from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { ENUM__segmentation_model } from '../_enums/ENUM__segmentation_model';
import { DTO__image_crop_box } from './DTO__image_crop_box';

export class DTO__image_segment extends DTO__base {
  /*
   * STATIC FIELDS
   */
  public static readonly _class_name_static = ENUM__dto_class_name.IMAGE_SEGMENT;
  /*
   * STATIC METHODS
   */

  static map_by_class_id(items: DTO__image_segment[]) {
    let out = new MAP__serializable<string, DTO__image_segment>();

    for (let item of items) {
      out.set(item.class_id, item);
    }

    return out;
  }

  static map_by_hash_content(items: DTO__image_segment[]) {
    //key - hash content
    let out = new Map<string, DTO__image_segment[]>();

    for (let item of items) {
      out.add_item_to_array(item.hash_sha256_content_source, item);
    }

    return out;
  }

  static are_all_disabled(items: DTO__image_segment[], disabled_ids: string[]) {
    if (disabled_ids.length < items.length) return false;

    let disabled_ids_set = new Set(disabled_ids);
    let items_ids = items.map(item => item.class_id);
    let items_ids_set = new Set(items_ids);

    let out = true;

    for (let item_id of items_ids_set) {
      if (!disabled_ids_set.has(item_id)) {
        out = false;
        break;
      }
    }

    return out;
  }

  /*
   * FIELDS
   */
  hash_sha256_content_source!: string;
  provider!: ENUM__segmentation_model;

  class_id!: string;
  class_label!: string;
  confidence_score!: number;

  /*
   * MASK
   */
  mask_width!: number;
  mask_height!: number;

  mask_base64!: string;

  bounding_box: DTO__image_crop_box;

  /*
   * PROPERTIES
   */
  get pixels_total() {
    return this.mask_width * this.mask_height;
  }

  /*
   * METHODS
   */

  constructor() {
    super();

    this.bounding_box = DTO__image_crop_box.ZERO.clone();
  }

  accumulate_to_mask(mask_cccumulator: Uint8Array = new Uint8Array(0)): Uint8Array {
    if (mask_cccumulator.length === 0) mask_cccumulator = new Uint8Array(this.pixels_total);
    assert_equal(this.pixels_total, mask_cccumulator.length);

    // 1. Decode base64 string to raw binary string
    const bytes_string = atob(this.mask_base64);

    const bytes = new Uint8Array(bytes_string.length);
    for (let i = 0; i < bytes_string.length; i++) bytes[i] = bytes_string.charCodeAt(i);

    // 2. Unpack bits directly into the accumulator
    const pixels_total = this.pixels_total;
    let pixel_index = 0;

    for (let i = 0; i < bytes.length && pixel_index < pixels_total; i++) {
      const byte = bytes[i];

      // Extract 8 bits from each byte
      for (let bit = 7; bit >= 0 && pixel_index < pixels_total; bit--) {
        const is_masked = (byte >> bit) & 1;

        // Only accumulate active ("black") pixels; leave 0s untouched
        // so other segments aren't accidentally wiped out.
        if (is_masked === 1) {
          mask_cccumulator[pixel_index] = 1;
        }

        pixel_index++;
      }
    }

    return mask_cccumulator;
  }

  public assert_standard_fields(): void {
    assert_hash_sha256_not_empty(this.hash_sha256_content_source);

    assert_type_enum(this.provider, ENUM__segmentation_model);

    assert_string_not_empty(this.class_id);

    assert_is_safe_number(this.confidence_score);

    assert_is_safe_number(this.mask_width);
    assert_is_safe_number(this.mask_height);

    assert_string_not_empty(this.mask_base64);
  }
}
